import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { PortalCommunicationComposite } from "@/Common/Models/PortalCommunication/PortalCommunicationComposite";
import { CommunicationsUIStateInbox } from "@/Common/Models/UI/Enums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PortalCommunicationInboxFilterSetting, PortalCommunicationSentFilterSetting } from "@/Communications/Models/PortalCommunication/PortalCommunicationFilterSettings";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { IGetPortalCommunicationsResponse } from "../../../Common/Api/SharedItem/Response/IGetPortalCommunicationsResponse";
import { SharedItemNote } from "../../../Common/Models/Portal/SharedItem/SharedItemNote";
import { IGetPortalCommunicationsRequest } from "./Requests/IGetPortalCommunicationsRequest";
import { IGetSharedItemNotesResponse } from "./Responses/IGetSharedItemNotesResponse";

class PortalCommunicationsClient {
    /**
     * Get a list of portal communications based on the request.
     * @param request A request to fetch portal communications.
     * @returns An object containing:
     * - `portalCommunicationComposites`: An array of `PortalCommunicationComposite` objects created from the response data.
     * - `portalCommunicationSharedItemIds`: An array of shared item IDs extracted from the portal communication composites.
     * - `totalCount`: The total count of portal communications.
     * If there are errors in the response, a popup is shown and `null` is returned.
     * If the response data does not contain portal communication composites, an object with empty arrays and a total count of 0 is returned.
     */
    public static async fetchPortalCommunications(request: IGetPortalCommunicationsRequest): Promise<{
        portalCommunicationComposites: PortalCommunicationComposite[]
        portalCommunicationSharedItemIds: string[],
        totalCount: number
    }> {
        const response = await Client.postAsync<IGetPortalCommunicationsResponse>(
            ServerOperations.Server.Myle_PortalCommunication_GetPortalCommunications,
            request
        );

        return this.handlePortalCommunicationsResponse(response);
    }
    /**
     * Handles the response from the portal communications API and processes the data into a structured format.
     * @param response - The client response containing the portal communications data or errors.
     * @returns An object containing:
     * - `portalCommunicationComposites`: An array of `PortalCommunicationComposite` objects created from the response data.
     * - `portalCommunicationSharedItemIds`: An array of shared item IDs extracted from the portal communication composites.
     * - `totalCount`: The total count of portal communications.
     * If there are errors in the response, a popup is shown and `null` is returned.
     * If the response data does not contain portal communication composites, an object with empty arrays and a total count of 0 is returned.
     */
    private static handlePortalCommunicationsResponse(response: ClientResponse<IGetPortalCommunicationsResponse>): {
        portalCommunicationComposites: PortalCommunicationComposite[]
        portalCommunicationSharedItemIds: string[],
        totalCount: number
    } {
        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        if (response.data.PortalCommunicationComposites) {
            return {
                portalCommunicationComposites: response.data.PortalCommunicationComposites
                    .map(PortalCommunicationComposite.fromResponseModel),
                portalCommunicationSharedItemIds: response.data.PortalCommunicationComposites
                    .map(p => p.SharedItem.SharedItemId),
                totalCount: response.data.TotalCount
            };
        }

        return {
            portalCommunicationComposites: [],
            portalCommunicationSharedItemIds: [],
            totalCount: 0
        };
    }

    /**
     * Save the filter setting of the table in portal communication.
     * @param filterSetting A filter setting to save in the user setting.
     * @param inboxType The type of inbox for which the filter setting is being saved.
     */
    public static async savePortalCommunicationFilterSetting(
        filterSetting: PortalCommunicationInboxFilterSetting,
        inboxType: CommunicationsUIStateInbox
    ): Promise<void> {
        const filterSettingModel = PortalCommunicationInboxFilterSetting.toResponseModel(filterSetting, true);
        let filterSettingResponseModel: UserSettings;

        switch (inboxType) {
            case CommunicationsUIStateInbox.Inbox:
                filterSettingResponseModel = new UserSettings({
                    PortalCommunicationInboxFilterSetting: filterSettingModel
                });
                break;
            case CommunicationsUIStateInbox.Archive:
                filterSettingResponseModel = new UserSettings({
                    PortalCommunicationArchivedFilterSetting: filterSettingModel
                });
                break;
            case CommunicationsUIStateInbox.FlaggedForReview:
                filterSettingResponseModel = new UserSettings({
                    PortalCommunicationFlaggedFilterSetting: filterSettingModel
                });
                break;
            default:
                return;
        }

        if (!filterSettingResponseModel) {
            return;
        }

        await Client.postAsync(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: filterSettingResponseModel,
                UserId: sessionStore.userId
            }
        );
    }

    /**
     * Save the filter setting of the sent table in portal communication.
     * @param filterSetting A filter setting to save in the user setting.
     */
    public static async savePortalCommunicationSentFilterSetting(
        filterSetting: PortalCommunicationSentFilterSetting
    ): Promise<void> {
        const userSettings = new UserSettings({
            PortalCommunicationSentFilterSetting: PortalCommunicationSentFilterSetting.toResponseModel(filterSetting, true)
        });

        await Client.postAsync(ServerOperations.Server.Myle_UpdateUserSettings, {
            UserSettings: userSettings,
            UserId: sessionStore.userId
        });
    }

    /**
     * Loads the notes for a shared item / portal communication thread.
     * @param sharedItemId The ID of the shared item.
     */
    public static async loadSharedItemNotes(sharedItemId: string): Promise<SharedItemNote[]> {
        const response = await Client.postAsync<IGetSharedItemNotesResponse>(ServerOperations.Server.Myle_PortalCommunication_LoadSharedItemNotes, { SharedItemId: sharedItemId });

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        if (response.data.SharedItemNotes) {
            return response.data.SharedItemNotes.map(SharedItemNote.fromResponseModel);
        }

        return [];
    }
}

export { PortalCommunicationsClient };
